import { differenceInCalendarDays, parseISO } from 'date-fns';
import { NOTIFICATION_CATEGORIES, type NotificationDto } from '@nexops/shared';
import type { MessageKey } from '@/i18n/I18nProvider';
import { CATEGORY_STYLE } from './category-style';
import type { GroupMode } from '../hooks/useNotificationsList';

export interface NotificationGroup {
  key: string;
  labelKey: MessageKey;
  items: NotificationDto[];
}

type DayBucket = 'pinned' | 'today' | 'yesterday' | 'week' | 'older';
const DAY_LABEL: Record<DayBucket, MessageKey> = {
  pinned: 'notifs.group.pinned',
  today: 'notifs.group.today',
  yesterday: 'notifs.group.yesterday',
  week: 'notifs.group.week',
  older: 'notifs.group.older',
};
const DAY_ORDER: DayBucket[] = ['pinned', 'today', 'yesterday', 'week', 'older'];

function dayBucket(n: NotificationDto, now: Date): DayBucket {
  if (n.pinned) return 'pinned';
  const diff = differenceInCalendarDays(now, parseISO(n.createdAt));
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff <= 7) return 'week';
  return 'older';
}

/** Groups already-sorted rows (pinned first, newest first) either by day or by category; empty groups are dropped. */
export function groupNotifications(items: NotificationDto[], mode: GroupMode): NotificationGroup[] {
  if (mode === 'category') {
    return NOTIFICATION_CATEGORIES.map((c) => ({ key: c, labelKey: CATEGORY_STYLE[c].labelKey, items: items.filter((n) => n.category === c) })).filter(
      (g) => g.items.length > 0,
    );
  }
  const now = new Date();
  const buckets = new Map<DayBucket, NotificationDto[]>();
  for (const n of items) {
    const b = dayBucket(n, now);
    buckets.set(b, [...(buckets.get(b) ?? []), n]);
  }
  return DAY_ORDER.filter((b) => buckets.has(b)).map((b) => ({ key: b, labelKey: DAY_LABEL[b], items: buckets.get(b)! }));
}

/** True for in-app paths (`/customers/1`), false for absolute http(s) URLs. */
export const isInternalLink = (link: string) => link.startsWith('/') && !link.startsWith('//');
